<script setup>
import { computed } from 'vue'
import { Head, usePage } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import PublicLayout from '@/Layouts/PublicLayout.vue'

defineOptions({ layout: PublicLayout })

const { t } = useI18n()
const page     = usePage()
const channels = computed(() => page.props.brand?.channels || {})
// "Write us" CTAs come from message-type channels. Social pages render
// in a separate Follow-us block below.
const writeUsChannels = computed(() => channels.value.message || [])
const followUsChannels = computed(() => channels.value.social_page || [])

function platformLabel(p) {
  // Display the platform name with first letter capitalized.
  return p.charAt(0).toUpperCase() + p.slice(1)
}
// Extract a short handle from a saved URL so the card shows something
// readable below the platform name (e.g. "@get2phuket", "vk.com/getphuket")
// without us having to store a separate handle field.
function urlHint(url) {
  if (!url) return ''
  try {
    const u = new URL(url)
    const pathTail = u.pathname.replace(/\/+$/, '').split('/').pop() || u.hostname
    return pathTail.startsWith('@') ? pathTail : `${u.hostname.replace(/^www\./, '')}${u.pathname}`.replace(/\/+$/, '')
  } catch {
    return url
  }
}
</script>

<template>
  <Head>
    <title>{{ t('public.head_title') }}</title>
    <meta name="description" :content="t('public.head_description')">
    <meta property="og:title"       :content="t('public.head_title')">
    <meta property="og:description" :content="t('public.head_description')">
    <meta property="og:type"        content="website">
  </Head>

  <section class="hero">
    <div class="hero__inner">
      <p class="hero__eyebrow">{{ t('public.eyebrow') }}</p>
      <h1 class="hero__title">{{ t('public.headline') }}</h1>
      <p class="hero__lede">{{ t('public.lede') }}</p>

      <ul class="hero__pillars">
        <li class="pillar">
          <a href="/rentals" class="pillar__link">
            <span class="pillar__dot pillar__dot--rentals"></span>
            <span>{{ t('public.pillars.rentals') }}</span>
          </a>
        </li>
        <li class="pillar">
          <a href="/property" class="pillar__link">
            <span class="pillar__dot pillar__dot--property"></span>
            <span>{{ t('public.pillars.property') }}</span>
          </a>
        </li>
        <li class="pillar">
          <a href="/tours" class="pillar__link">
            <span class="pillar__dot pillar__dot--tours"></span>
            <span>{{ t('public.pillars.tours') }}</span>
          </a>
        </li>
      </ul>

      <p v-if="writeUsChannels.length" class="hero__cta">{{ t('public.cta_lede') }}</p>
      <div v-if="writeUsChannels.length" class="hero__channels">
        <a
          v-for="c in writeUsChannels"
          :key="c.id"
          class="channel channel--write"
          :href="c.url"
          target="_blank"
          rel="noopener"
        >
          <span class="channel__name">{{ t('public.write_on', { platform: platformLabel(c.platform) }) }}</span>
          <span class="channel__handle">{{ c.label || urlHint(c.url) }}</span>
        </a>
      </div>

      <template v-if="followUsChannels.length">
        <p class="hero__cta hero__cta--follow">{{ t('public.follow_us') }}</p>
        <div class="hero__channels hero__channels--follow">
          <a
            v-for="c in followUsChannels"
            :key="c.id"
            class="channel"
            :href="c.url"
            target="_blank"
            rel="noopener"
          >
            <span class="channel__name">{{ platformLabel(c.platform) }}</span>
            <span class="channel__handle">{{ c.label || urlHint(c.url) }}</span>
          </a>
        </div>
      </template>
    </div>
  </section>
</template>

<style scoped>
.hero {
  flex: 1;
  display: grid;
  place-items: center;
  padding: 3rem 1.5rem 4rem;
}

.hero__inner {
  max-width: 36rem;
  width: 100%;
  text-align: center;
}

.hero__eyebrow {
  font-size: 0.75rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--accent);
  margin: 0 0 1rem;
  font-weight: 600;
}

.hero__title {
  font-family: var(--font-display, var(--display));
  font-size: clamp(2rem, 6vw, 3rem);
  line-height: 1.1;
  margin: 0 0 1rem;
  color: var(--ink);
  font-weight: 700;
  letter-spacing: -0.01em;
}

.hero__lede {
  font-size: 1.0625rem;
  line-height: 1.6;
  color: var(--inkSoft);
  margin: 0 auto 2rem;
  max-width: 32rem;
}

.hero__pillars {
  list-style: none;
  padding: 0;
  margin: 0 auto 2.5rem;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.5rem 1rem;
  font-size: 0.9375rem;
  color: var(--ink);
}
.pillar {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0.8rem;
  border-radius: 999px;
  background: var(--surface);
  border: 1px solid var(--line);
}
.pillar__dot {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 999px;
  background: var(--muted);
}
.pillar__dot--rentals  { background: var(--accent); }
.pillar__dot--property { background: var(--ok); }
.pillar__dot--tours    { background: var(--warn); }

.pillar__link {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: inherit;
  text-decoration: none;
}

.hero__cta {
  font-size: 0.875rem;
  color: var(--muted);
  margin: 0 0 1rem;
}

.hero__channels {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(11rem, 1fr));
  gap: 0.625rem;
  max-width: 32rem;
  margin: 0 auto;
}

.channel {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0.75rem 0.875rem;
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: 0.75rem;
  color: var(--ink);
  text-decoration: none;
  transition: border-color 150ms ease, transform 150ms ease;
}
.channel:hover {
  border-color: color-mix(in oklch, var(--accent) 60%, var(--line));
  transform: translateY(-1px);
}
.channel__name {
  font-weight: 600;
  font-size: 0.875rem;
}
.channel__handle {
  font-size: 0.75rem;
  color: var(--muted);
  margin-top: 0.125rem;
}
.channel--write {
  background: var(--accent);
  color: var(--accentInk);
  border-color: var(--accent);
}
.channel--write .channel__handle { color: var(--accentInk); opacity: 0.85; }
.channel--write:hover { border-color: var(--accent); }

.hero__cta--follow { margin-top: 2rem; }
.hero__channels--follow { grid-template-columns: repeat(auto-fit, minmax(9rem, 1fr)); }
</style>
